import {CopilotReferencePreviewDialog} from '@github-ui/copilot-reference-preview/CopilotReferencePreviewDialog'
import {addUrlToHistoryStack} from '@github-ui/history'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {PathFunction} from '@github-ui/paths'
import {type ReactPartialAnchorProps, useOnAnchorClick} from '@github-ui/react-core/react-partial-anchor'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, ScreenSizeProvider, useScreenSize} from '@github-ui/screen-size'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {CopilotIcon} from '@primer/octicons-react'
import {Button, IconButton, Popover} from '@primer/react'
import {Tooltip} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {RelayEnvironmentProvider} from 'react-relay'

import {Chat} from './components/Chat'
import {ChatPanel} from './components/ChatPanel'
import {Header} from './components/Header'
import {ChatPortalContainer} from './components/PortalContainerUtils'
import {useEntitlement} from './components/quota/EntitlementContext'
import {ThreadListView} from './components/ThreadListView'
import styles from './CopilotChat.module.css'
import {copilotChatHeaderButtonID, copilotChatPanelID} from './utils/constants'
import type {AddCopilotChatReferenceEvent, SearchCopilotEvent, SymbolChangedEvent} from './utils/copilot-chat-events'
import {
  subscribeAddCopilotChatReference,
  subscribeOpenCopilotChat,
  subscribeSearchCopilot,
  subscribeSymbolChanged,
} from './utils/copilot-chat-events'
import {isRepository, makeRepositoryReference} from './utils/copilot-chat-helpers'
import {useResizablePanel} from './utils/copilot-chat-hooks'
import type {LoadingStateState} from './utils/copilot-chat-reducer'
import {
  type CopilotChatOrg,
  type CopilotChatPayload,
  type CopilotChatRepo,
  CopilotLicenseType,
  DialogType,
} from './utils/copilot-chat-types'
import {copilotLocalStorage} from './utils/copilot-local-storage'
import {CopilotChatProvider, useChatState} from './utils/CopilotChatContext'
import {useChatManager} from './utils/CopilotChatManagerContext'
import {getSelectedThread} from './utils/get-selected-thread'
import {useDefaultModel} from './utils/models'

export interface CopilotChatProps extends CopilotChatPayload, ReactPartialAnchorProps {
  currentTopic?: CopilotChatRepo
  findFileWorkerPath: string
  ssoOrganizations: CopilotChatOrg[]
  renderPopover?: boolean
  chatIsVisible?: boolean
  chatVisibleSettingPath?: string
  realIp: string
}

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

export function CopilotChat(props: CopilotChatProps) {
  return (
    <RelayEnvironmentProvider environment={environment}>
      <CopilotChatNoRelay {...props} />
    </RelayEnvironmentProvider>
  )
}

// This component is used to render the assistive experience without the relay environment
// This is useful for testing purposes when the relay environment is provided by renderRelay test helper
export function CopilotChatNoRelay(props: CopilotChatProps) {
  const copilotButtonRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const selectedThreadID = copilotLocalStorage.selectedThreadID
  const currentReferences = copilotLocalStorage.getCurrentReferences(selectedThreadID) || []

  return (
    <ScreenSizeProvider>
      <CopilotChatProvider
        topic={props.currentTopic}
        workerPath={props.findFileWorkerPath}
        threadId={selectedThreadID}
        refs={currentReferences}
        mode="assistive"
        ssoOrganizations={props.ssoOrganizations}
        chatIsOpen={false}
        chatIsVisible={props.chatIsVisible}
        chatVisibleSettingPath={props.chatVisibleSettingPath}
        realIp={props.realIp}
        copilotChatPayload={props}
      >
        <CopilotHeaderButton
          renderPopover={props.renderPopover || false}
          ref={copilotButtonRef}
          reactPartialAnchor={props.reactPartialAnchor}
          inputRef={inputRef}
        />
        <ChatPortalContainer />
        <ChatPanelWithHeader inputRef={inputRef} />
      </CopilotChatProvider>
    </ScreenSizeProvider>
  )
}

export const dismissNoticePath: PathFunction<{notice: string}> = ({notice}) => `/settings/dismiss-notice/${notice}`

type CopilotHeaderButtonProps = {
  renderPopover: boolean
  inputRef: React.RefObject<HTMLTextAreaElement>
} & ReactPartialAnchorProps
const CopilotHeaderButton = React.forwardRef<HTMLButtonElement, CopilotHeaderButtonProps>((props, ref) => {
  const manager = useChatManager()
  const state = useChatState()
  const selectedThread = getSelectedThread(state)
  const {currentTopic, currentView} = state
  const {screenSize} = useScreenSize()
  const {licenseType} = useEntitlement()
  const defaultModel = useDefaultModel(state.availableModels)
  const initialRenderRef = useRef(true)

  useEffect(() => {
    return subscribeOpenCopilotChat(
      e => void manager.handleOpenPanelEvent(selectedThread, e, state.chatIsOpen, currentTopic, defaultModel),
    )
  }, [currentTopic, selectedThread, manager, state.chatIsOpen, defaultModel])

  useEffect(() => {
    return subscribeAddCopilotChatReference((e: AddCopilotChatReferenceEvent) => {
      void manager.handleAddReferenceEvent(e)
    })
  }, [selectedThread, manager, state.currentReferences])

  useEffect(() => {
    return subscribeSymbolChanged((e: SymbolChangedEvent) => {
      void manager.handleSymbolChangedEvent(e)
    })
  })

  useEffect(() => {
    return subscribeSearchCopilot((e: SearchCopilotEvent) => {
      void manager.handleSearchCopilotEvent(e, defaultModel)
    })
  }, [manager, defaultModel])

  useEffect(() => {
    const url = new URL(window.location.href, window.location.origin)
    const params = url.searchParams
    const threadId = state.selectedThreadID
    // copilot=1 url param means that we have opened the page with the intention to open the chat
    // with the threadId that was set in the immersive view
    if (params.get('copilot') === '1' && threadId) {
      const thread = state.threads.get(threadId) ?? null
      void manager.openChat(thread, currentView, 'immersive', state.chatVisibleSettingPath)
      url.searchParams.delete('copilot')
      addUrlToHistoryStack(url.toString())
    }
  }, [manager, state.selectedThreadID, state.threads, currentView, state.chatVisibleSettingPath])

  // Run once on initial render to check if we should open chat on page load
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false
      if (state.chatIsVisible && !copilotLocalStorage.getCollapsedState() && screenSize > ScreenSize.large) {
        void manager.openChat(selectedThread, state.currentView, 'page load', state.chatVisibleSettingPath)
      }
    }
  }, [manager, screenSize, selectedThread, state.chatIsVisible, state.chatVisibleSettingPath, state.currentView])

  const openChat = useCallback(async () => {
    if (!state.chatIsOpen) {
      if (licenseType === CopilotLicenseType.Unlicensed) {
        if (state.chatVisibleSettingPath) {
          const data = new FormData()
          data.set('copilot_chat_visible', 'true')
          await verifiedFetch(state.chatVisibleSettingPath, {method: 'PUT', body: data})
          copilotLocalStorage.setCollapsedState(false)
        }
        window.location.replace(`${window.location.origin}/github-copilot/signup?return_to=${window.location.pathname}`)
        return
      }
      void manager.openChat(
        selectedThread,
        currentView,
        'header',
        state.chatVisibleSettingPath,
        isRepository(currentTopic) ? makeRepositoryReference(currentTopic) : undefined,
      )
    } else if (props.inputRef.current) {
      props.inputRef.current.focus()
    }
  }, [
    currentTopic,
    currentView,
    licenseType,
    manager,
    props.inputRef,
    selectedThread,
    state.chatIsOpen,
    state.chatVisibleSettingPath,
  ])

  if (props.reactPartialAnchor) {
    return (
      <div className={styles.CopilotChatContainer}>
        <ExternalAnchorListener reactPartialAnchor={props.reactPartialAnchor} />
        {props.renderPopover ? <WelcomePopover renderPopover /> : <></>}
      </div>
    )
  }

  return (
    <div className={clsx('AppHeader-CopilotChatButton', styles.CopilotChatContainer)}>
      <Tooltip aria-label="Chat with Copilot" direction="s">
        <div>
          {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
          <IconButton
            unsafeDisableTooltip
            ref={ref}
            id={copilotChatHeaderButtonID}
            icon={CopilotIcon}
            aria-label="Chat with Copilot"
            aria-controls={copilotChatPanelID}
            onClick={openChat}
            className={clsx('AppHeader-button', styles.IconButton)}
            data-testid="copilot-chat-button"
            data-hotkey="Shift+C"
            aria-expanded={false}
          />
        </div>
      </Tooltip>
      <WelcomePopover renderPopover={props.renderPopover} />
    </div>
  )
})

function ExternalAnchorListener({reactPartialAnchor}: ReactPartialAnchorProps) {
  const onClick = useCallback(() => {
    sendEvent('dotcom_chat.activate', {
      target: `GLOBAL_COPILOT_MENU_HEADER_TO_IMMERSIVE`,
      mode: 'global_nav',
    })
  }, [])

  useOnAnchorClick(reactPartialAnchor!, onClick)
  return <></>
}

function WelcomePopover({renderPopover}: {renderPopover: boolean}) {
  const [showPopover, setShowPopover] = useState(renderPopover)

  const handleClosePopover = useCallback(async () => {
    setShowPopover(false)
    await verifiedFetch(dismissNoticePath({notice: 'copilot_chat_new_user_popover'}), {method: 'POST'})
  }, [setShowPopover])

  return (
    <Popover open={showPopover} caret="top-right" className={styles.Popover}>
      <Popover.Content data-testid="copilot-chat-cta-popover" className={styles.Popover_Content}>
        <p>
          You now have access to{' '}
          <a
            href="https://docs.github.com/enterprise-cloud@latest/copilot/github-copilot-enterprise/overview/about-github-copilot-enterprise"
            target="_blank"
            rel="noopener noreferrer"
          >
            Copilot Enterprise
          </a>
          {'. '}
          Use the Copilot icon to get started.
        </p>
        <Button data-testid="dismiss-copilot-chat-cta-popover" onClick={handleClosePopover}>
          Got it!
        </Button>
      </Popover.Content>
    </Popover>
  )
}

CopilotHeaderButton.displayName = 'CopilotHeaderButton'

interface ChatPanelWithHeaderProps {
  inputRef: React.RefObject<HTMLTextAreaElement>
}

function ChatPanelWithHeader({inputRef}: ChatPanelWithHeaderProps) {
  const state = useChatState()
  const manager = useChatManager()
  const staffDialogRef = useRef<HTMLDivElement>(null)
  const [showStaffDialog, setShowStaffDialog] = useState<DialogType>(DialogType.None)
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false)
  const prevMessageCount = useRef<number>(state.messages.length)
  const prevMessageLoadingState = useRef<LoadingStateState>(state.messagesLoading.state)
  const {panelWidth, panelHeight, startResize, onResizerKeyDown} = useResizablePanel()

  useEffect(() => {
    if (
      state.messages.length > prevMessageCount.current &&
      !state.chatIsOpen &&
      prevMessageLoadingState.current === 'loaded'
    ) {
      setHasUnreadMessages(true)
    }
    prevMessageCount.current = state.messages.length
    prevMessageLoadingState.current = state.messagesLoading.state
  }, [state.chatIsOpen, state.messages.length, state.messagesLoading.state])

  useEffect(() => {
    if (state.chatIsOpen && hasUnreadMessages) {
      setHasUnreadMessages(false)
    }
  }, [hasUnreadMessages, state.chatIsOpen])

  return (
    <ChatPanel
      staffDialogRef={staffDialogRef}
      handleClose={(usedEscapeKey?: boolean) => {
        setShowStaffDialog(DialogType.None)
        manager.closeChat()
        const entryId = state.entryPointId ?? copilotChatHeaderButtonID

        if (usedEscapeKey && entryId) {
          const entryElement = document.getElementById(entryId)
          const scrollX = window.scrollX
          const scrollY = window.scrollY

          // Persist current scroll position by delaying focus
          setTimeout(() => {
            entryElement?.focus()
            window.scrollTo(scrollX, scrollY)
          }, 0)
        }
      }}
      panelWidth={panelWidth}
      panelHeight={panelHeight}
      startResize={startResize}
      onResizerKeyDown={onResizerKeyDown}
      initialFocusRef={inputRef}
    >
      <Header
        staffDialogRef={staffDialogRef}
        showStaffDialog={showStaffDialog}
        setShowStaffDialog={setShowStaffDialog}
        isResponding={state.messages.length <= 1 && (!!state.streamingMessage || state.isWaitingOnCopilot)}
      />
      {state.chatIsOpen && (
        <div className={styles.chatContentScrollContainer}>
          <div className={styles.chatViewContainer}>
            {state.currentView === 'thread' ? (
              <>
                <Chat key={state.selectedThreadID} inputRef={inputRef} />
                <CopilotReferencePreviewDialog />
              </>
            ) : (
              <ThreadListView />
            )}
          </div>
        </div>
      )}
    </ChatPanel>
  )
}

ChatPanelWithHeader.displayName = 'ChatPanelWithHeader'

try{ CopilotChat.displayName ||= 'CopilotChat' } catch {}
try{ CopilotChatNoRelay.displayName ||= 'CopilotChatNoRelay' } catch {}
try{ ExternalAnchorListener.displayName ||= 'ExternalAnchorListener' } catch {}
try{ WelcomePopover.displayName ||= 'WelcomePopover' } catch {}